<template>
  <div class="ac-card-content">
    <div class="ac-header-container">
      <div class="info-content-container flex-column">
        <span class="sac__font-18 bold"> Comfort Credits Services Mapping </span>
      </div>
      <button type="button" class="ac-close-button" @click="toggle(true, 'titan-edit-integration')">
        <img src="@shared/assets/img/general/x-black.png" alt="x" class="ac-close" />
      </button>
    </div>
    <div class="ac-body-container">
      <div class="grid-row gap-16">
        <p class="sac__font-14">
          Select and map the ServiceTitan pricebook services that represent Comfort Credits to
          ensure proper tracking in SmartAC.com. This allows you to designate which service entries
          in ServiceTitan should be recognized as Comfort Credits in SmartAC.com.
        </p>
        <p class="sac__font-14 semi-bold">
          Select the services from your ServiceTitan pricebook that should be treated as Comfort
          Credits. Use the “Add Another Service” button to include more services, or click the trash
          icon to remove any that were already added.
        </p>
        <div class="grid-column ai-c">
          <div class="grid-column ai-c jc-s gap-16">
            <div class="sac__font-16 semi-bold">ServiceTitan Services</div>
            <img
              src="@shared/assets/img/general/mapping-icon.png"
              alt="arrows"
              class="img-size-24"
            />
            <div class="sac__font-16 semi-bold grid-item js-c">Comfort Credits</div>
          </div>
        </div>
        <div
          v-for="(service, idx) in serviceMappings"
          :key="idx"
          class="grid-column ai-c jc-s gap-16"
        >
          <div style="width: 450px">
            {{ service.internalId }}
          </div>
          <button type="button" class="grid-item js-s" @click="removeService(service.externalId)">
            <img
              src="@shared/assets/img/general/trash-red.svg"
              class="img-size-20 clickable"
              alt="remove"
            />
          </button>
        </div>
        <div v-if="showInput" style="width: 450px">
          <SACAutoComplete
            placeholder="Type Service..."
            label="displayName"
            :searchMethod="(search) => getServiceList(search)"
            queryKey="search"
            :select="(s) => selectService(s)"
          />
        </div>
        <button type="button" class="sac__button bg-primary grid-item js-s" @click="addService">
          + Add another service
        </button>
      </div>
    </div>
    <div class="ac-footer-container">
      <button
        type="button"
        class="info-form-button-outline mr-2"
        @click="toggle(true, 'titan-edit-integration')"
      >
        Cancel
      </button>
      <button type="button" class="info-form-button" @click="submit">Save</button>
    </div>
    <ProcessOverlay :show="loadingServices" text="Saving..." />
  </div>
</template>

<script>
import customerDetailMixin from '@shared/mixins/customerDetailMixin';
import { mapState } from 'vuex';
import SACAutoComplete from '@shared/components/sac/SACAutoComplete.vue';
import ProcessOverlay from '@shared/components/ProcessOverlay.vue';

export default {
  name: 'MapCCServiceTitanPopup',
  components: {
    ProcessOverlay,
    SACAutoComplete,
  },
  mixins: [customerDetailMixin],
  props: {
    toggle: Function,
  },
  data() {
    return {
      serviceMappings: [],
      loadingServices: false,
      isAddingService: false,
      errorMessage: null,
    };
  },
  computed: {
    ...mapState({
      serviceOptions: (state) => state.serviceTitan.services,
      invoiceMappings: (state) => state.serviceTitan.invoiceMappings,
    }),
    showInput() {
      return this.isAddingService || !this.serviceMappings.length;
    },
  },
  watch: {
    invoiceMappings: {
      handler(newVal, oldVal) {
        this.serviceMappings =
          Array.isArray(newVal) && newVal.length
            ? newVal.map((v) => ({ externalId: Number(v.externalId), internalId: v.internalId }))
            : [];
      },
      deep: true,
      immediate: true,
    },
  },
  async mounted() {
    this.$store.dispatch('getServices');
  },
  methods: {
    async getServiceList(search) {
      const regex = new RegExp(search, 'i');
      return this.serviceOptions.filter(
        (service) => service.displayName && regex.test(service.displayName),
      );
    },
    addService() {
      this.isAddingService = true;
    },
    removeService(externalId) {
      this.serviceMappings = this.serviceMappings.filter((item) => item.externalId !== externalId);
    },
    selectService(item) {
      const isAdded = this.serviceMappings.find((s) => s.externalId === item.id);
      if (!isAdded) {
        this.serviceMappings = [
          ...this.serviceMappings,
          { internalId: item.displayName, externalId: item.id },
        ];
      }
      this.isAddingService = false;
    },
    async submit() {
      this.loadingServices = true;
      const addPayload = this.serviceMappings.map((item) => ({
        externalId: item.externalId,
        internalId: item.internalId,
        mapType: 'ComfortCreditDeduction',
        externalSource: 'Service Titan',
      }));

      const deletePayload = this.invoiceMappings
        .filter(
          (item) =>
            !this.serviceMappings.some((s) => Number(s.externalId) === Number(item.externalId)),
        )
        .map((item) => ({
          ...item,
          externalId: -1,
        }));
      const payload = [...addPayload, ...deletePayload];
      await this.$store.dispatch('saveMapping', payload);
      this.loadingServices = false;
      this.toggle(true, 'titan-edit-integration');
    },
  },
};
</script>

<style lang="scss" scoped>
.ac-card-content {
  .ac-header-container {
    display: flex;
    justify-content: space-between;
    .info-content-container {
      align-items: flex-start;
      display: inline-flex;
      gap: 8px;
      width: 100%;
    }
  }
  .info-form-button {
    padding: 12px;
    height: 48px;
    border-radius: 6px;
    background-color: $NBLUE100;
    font-weight: 600;
    color: $WHITE;
    width: 155px;
  }
  .info-form-button-outline {
    padding: 12px;
    height: 48px;
    border-radius: 4px;
    border: 2px solid $NBLUE100;
    color: $NBLUE100;
    font-weight: 600;
    width: 155px;
  }
}
</style>
